import styles from "./Monthly.module.css";
import { useTranslation } from "@coworker/locales";
import { FixaInputField } from "@coworker/apprestructured/src/shared/wrappers/FixaInputField/FixaInputField";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import { useMemo } from "react";
import { FixaListView } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListView";
import { FixaListViewItem } from "@coworker/apprestructured/src/shared/wrappers/FixaListView/FixaListViewItem";
import { DatePicker } from "@coworker/apprestructured/src/shared/components/DatePicker/DatePicker";
import dayjs from "dayjs";
import {
  calcIsFifthWeekdayOfMonth,
  calcIsLastWeekdayOfMonth,
  calculateWeekdayOrdinalTranslationKey,
  getWeekdayNumber,
  getWeekdays,
} from "@coworker/apprestructured/src/task-management/components/ScheduleTask/helper";
import { MonthlyRecurrenceType } from "@coworker/apprestructured/src/task-management/types/Schedule";
import InputWrapper from "@coworker/apprestructured/src/task-management/components/InputWrapper/InputWrapper";

type MonthlyProps = {
  start: string;
  end: string;
  startDateError: string | null;
  endDateError: string | null;
  interval: string;
  type: MonthlyRecurrenceType;
  onStartChange: (start: string) => void;
  onEndChange: (end: string) => void;
  onIntervalChange: (interval: string) => void;
  onTypeChange: (option: MonthlyRecurrenceType) => void;
  intervalError: string | null;
};

export const Monthly = (props: MonthlyProps) => {
  const { t } = useTranslation();

  const today = useMemo(() => dayjs().format("YYYY-MM-DD"), []);

  const isLastWeekday = useMemo(
    () => calcIsLastWeekdayOfMonth(props.start),
    [props.start]
  );

  const isFifthWeekday = useMemo(
    () => calcIsFifthWeekdayOfMonth(props.start),
    [props.start]
  );

  const startWeekdayNumber = useMemo(
    () => (props.start ? getWeekdayNumber(props.start) : null),
    [props.start]
  );
  const weekdays = useMemo(() => getWeekdays(t), [t]);
  const startDateWeekday = useMemo(
    () => weekdays.find((day) => day.weekdayNumber === startWeekdayNumber),
    [startWeekdayNumber, weekdays]
  );

  return (
    <>
      <DatePicker
        label={t("startDateString")}
        value={props.start}
        onChange={(value) => props.onStartChange(value)}
        errorMessage={props.startDateError}
        min={today}
      />
      <div>
        <FixaText textStyle="LabelRegular.M" className={styles["label"]}>
          {t("thisTaskWillBeRepeatedEveryString")}
        </FixaText>
        <InputWrapper error={props.intervalError}>
          <FixaInputField
            className={styles["inputField"] as string}
            id="interval"
            type="number"
            value={props.interval}
            onChange={(e) => props.onIntervalChange(e.target.value)}
            suffixLabel={t("monthStringCapitalized")}
          />
        </InputWrapper>
      </div>
      {props.start && (
        <FixaListView id="monthlyOptions" size="small">
          {!isFifthWeekday && (
            <FixaListViewItem
              title={t("repeatOnOfMonthString", {
                value: props.start
                  ? `${t(calculateWeekdayOrdinalTranslationKey(props.start))} ${startDateWeekday?.weekday}`
                  : "-",
              })}
              control="radiobutton"
              onChange={() => {
                props.onTypeChange("weekday");
              }}
              controlProps={{
                checked: props.type === "weekday",
                value: "weekday",
              }}
            />
          )}
          {isLastWeekday && (
            <FixaListViewItem
              title={t("repeatOnOfMonthString", {
                value: `${t("ordinallastString")} ${startDateWeekday?.weekday}`,
              })}
              control="radiobutton"
              onChange={() => {
                props.onTypeChange("last_weekday");
              }}
              controlProps={{
                checked: props.type === "last_weekday",
                value: "last_weekday",
              }}
            />
          )}
        </FixaListView>
      )}
      <DatePicker
        label={t("endDateString")}
        value={props.end}
        onChange={(value) => props.onEndChange(value)}
        errorMessage={props.endDateError}
        min={today}
      />
    </>
  );
};
